/**
 * QuestionCard Component
 * Draggable, collapsible card for displaying and editing flow questions
 */

import React, { useState } from 'react';
import { Card, Tag, Button, Checkbox } from 'antd';
import { toast } from 'react-hot-toast';
import {
  EditOutlined,
  HolderOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IFlowQuestion } from '../../utils/jsonFlowGenerator';
import { SortableOption } from './SortableOption';

interface QuestionCardProps {
  question: IFlowQuestion & { helperText?: string };
  onEdit: (question: IFlowQuestion) => void;
  onUpdateOptionOrder: (questionId: string, newOrder: string[]) => void;
  isCustomized?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (questionId: string, checked: boolean) => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onEdit,
  onUpdateOptionOrder,
  isCustomized,
  isSelected = false,
  onToggleSelect,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.questionId });

  // DnD sensors for options
  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getQuestionTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      single_select: 'blue',
      multi_select: 'purple',
      text_input: 'green',
      number_input: 'cyan',
      boolean: 'orange',
      visual_select: 'magenta',
      swatch_select: 'geekblue',
    };
    return colors[type] || 'default';
  };

  const formatPrice = (price?: number, priceModifier?: number): string => {
    // If price exists, show as base price
    if (price !== undefined && price !== null) {
      return `£${price.toFixed(2)}`;
    }
    // If priceModifier exists, show with +/- sign
    if (priceModifier !== undefined && priceModifier !== null) {
      if (priceModifier === 0) return 'Included';
      return priceModifier > 0 ? `+£${priceModifier.toFixed(2)}` : `-£${Math.abs(priceModifier).toFixed(2)}`;
    }
    return 'Included';
  };

  // Handle option drag end
  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = question.options.findIndex(opt => opt.optionId === active.id);
      const newIndex = question.options.findIndex(opt => opt.optionId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new order from current displayed order
        const currentOrder = question.options.map(opt => opt.optionId);
        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        onUpdateOptionOrder(question.questionId, newOrder);
        toast.success('Option order updated');
      }
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        className={`border ${isSelected ? 'border-blue-500 bg-blue-50' : isCustomized ? 'border-blue-400' : 'border-gray-200'} ${
          isDragging ? 'shadow-lg' : 'shadow-sm'
        }`}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header - when selected the whole row is draggable */}
        <div
          className={`flex items-center p-3 ${isSelected ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
          onClick={() => !isSelected && setIsExpanded(!isExpanded)}
          {...(isSelected ? { ...attributes, ...listeners } : {})}
        >
          {/* Checkbox */}
          {onToggleSelect && (
            <div
              className="mr-2"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                onChange={(e) => onToggleSelect(question.questionId, e.target.checked)}
              />
            </div>
          )}

          {/* Drag Handle — always shown; primary drag target when not selected */}
          <div
            {...(!isSelected ? { ...attributes, ...listeners } : {})}
            className={`p-1 mr-2 ${isSelected ? 'text-blue-400' : 'cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <HolderOutlined style={{ fontSize: 16 }} />
          </div>

          {/* Question ID */}
          <Tag color="default" className="mr-2">
            {question.questionId.split('_')[0]}
          </Tag>

          {/* Question Text */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">
              {question.question}
              {isCustomized && (
                <Tag color="blue" className="ml-2" style={{ fontSize: 10 }}>
                  Customized
                </Tag>
              )}
            </div>
            {question.helperText && (
              <div className="text-xs text-blue-500 italic mt-0.5 truncate">
                {question.helperText}
              </div>
            )}
            <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
              <span>Step {question.step}</span>
              <span>|</span>
              <Tag color={getQuestionTypeColor(String(question.questionType))} style={{ fontSize: 10, margin: 0 }}>
                {question.questionType}
              </Tag>
              <span>|</span>
              <span className="font-medium text-gray-600">{question.optionTypeName}</span>
              <span>|</span>
              <span>{question.options.length} options</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-2">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(question);
              }}
            />
            <span
              onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
              className="cursor-pointer"
            >
              {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </span>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
            <div className="flex items-center gap-4 mb-2 text-xs flex-wrap">
              <div>
                <span className="text-gray-500">Category: </span>
                <span className="font-medium">{question.categoryName}</span>
              </div>
              <div>
                <span className="text-gray-500">Type: </span>
                <span className="font-medium">{question.optionTypeName}</span>
              </div>
              <div>
                <span className="text-gray-500">Required: </span>
                <Tag color={question.required ? 'green' : 'default'} style={{ fontSize: 10, margin: 0 }}>
                  {question.required ? 'Yes' : 'No'}
                </Tag>
              </div>
            </div>
            {question.helperText && (
              <div className="text-xs mb-2 bg-blue-50 p-1.5 rounded">
                <span className="text-gray-500">Helper: </span>
                <span className="text-gray-700">{question.helperText}</span>
              </div>
            )}

            {/* Options in Grid with Drag and Drop */}
            <div className="text-xs text-gray-500 mb-1 font-medium">
              Options ({question.options.length}) - Drag to reorder
            </div>
            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOptionDragEnd}
            >
              <SortableContext
                items={question.options.map(opt => opt.optionId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-3 gap-1 max-h-40 overflow-y-auto">
                  {question.options.map((option) => (
                    <SortableOption
                      key={option.optionId}
                      option={option}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </Card>
    </div>
  );
};
